import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Sparkles, Leaf, CheckCircle2 } from 'lucide-react';

interface ConsultationModalProps {
  data: any;
  onClose: () => void;
}

export const ConsultationModal: React.FC<ConsultationModalProps> = ({ data, onClose }) => {
  const [showChat, setShowChat] = useState(false);
  
  const handlePrint = () => { window.print(); };
  if (!data) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-sage-900/40 backdrop-blur-sm animate-in fade-in duration-300 print:relative print:inset-auto print:p-0 print:bg-white print:backdrop-blur-none">
      <div id="print-section" className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-8 print:shadow-none print:rounded-none print:max-w-none print:max-h-none print:overflow-visible print:block">
        {/* Header - Not printed */}
        <div className="flex items-center justify-between p-6 border-b border-sage-100 bg-sage-50/50 no-print">
          <div>
            <h2 className="text-2xl font-serif font-bold text-sage-900 capitalize">{data.ailment_name || data.ailment || "Consultation"}</h2>
            <p className="text-sm text-sage-500 font-medium mt-1">Saved on {new Date(data.saved_at).toLocaleDateString()}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <button onClick={handlePrint} className="px-4 py-2 bg-sage-600 text-white rounded-xl text-sm font-bold shadow-sm hover:bg-sage-700 transition-all">Print / Export PDF</button>
              <span className="text-[10px] text-sage-400 mt-1 font-bold tracking-tight">Use browser's 'Save as PDF'</span>
            </div>
            <button onClick={onClose} className="p-2 bg-white rounded-full text-sage-400 hover:text-sage-700 hover:bg-sage-100 transition-colors border border-sage-100 shadow-sm"><X size={20} /></button>
          </div>
        </div>

        {/* Print Only Header */}
        <div className="hidden print-only mb-8 border-b-2 border-sage-900 pb-4">
          <h1 className="text-3xl font-serif font-bold text-sage-900">NatureNani</h1>
          <h2 className="text-2xl font-serif text-sage-800 mt-4 capitalize">{data.ailment_name || data.ailment}</h2>
          <p className="text-sm text-sage-600">Saved on {new Date(data.saved_at).toLocaleDateString()}</p>
          <p className="text-xs text-sage-500 italic mt-2">This consultation is for educational purposes only and does not constitute medical advice.</p>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8 overflow-y-auto flex-1 bg-white print-table-container print:overflow-visible print:overflow-y-visible print:p-0 print:block">
          
          <div className="mb-8 p-6 bg-sage-50 border-l-4 border-sage-400 rounded-r-2xl">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-sage-500 mb-2">Your Question</h4>
            <p className="font-serif text-lg text-sage-900 italic">
              "{data.user_question || (data.chat_history?.find((m: any) => m.role === 'user')?.content) || "No question recorded."}"
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-sage-500 mb-4 flex items-center gap-2">
              <Sparkles size={14} className="text-sage-400" /> Nani's Consultation Response
            </h3>
            <div className="markdown-content prose prose-slate max-w-none text-left">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h3: ({ node, ...props }: any) => <h3 className="font-serif font-bold text-sage-800 text-lg mt-6 mb-3 border-b border-sage-50 pb-2" {...props} />,
                  p: ({ node, ...props }: any) => <p className="mb-4 last:mb-0 leading-relaxed text-gray-700" {...props} />,
                  table: ({ node, ...props }: any) => <div className="overflow-x-auto my-4 rounded-xl border border-sage-100 shadow-sm"><table className="min-w-full" {...props} /></div>,
                  th: ({ node, ...props }: any) => <th className="px-3 py-3 text-left text-[10px] font-bold text-white uppercase tracking-wider bg-sage-600" {...props} />,
                  td: ({ node, ...props }: any) => <td className="px-3 py-3 text-sm text-gray-700 border-b border-sage-50" {...props} />,
                }}
              >
                {data.nani_response || data.summary?.root_cause || "No response content found."}
              </ReactMarkdown>
            </div>
          </div>

          <div className="hidden print-only mt-12 pt-4 border-t border-sage-200 text-center text-xs font-bold text-sage-500 uppercase tracking-widest">
            NatureNani &mdash; Know Your Nature &mdash; naturenani.com
          </div>

          <div className="mt-8 border-t border-sage-100 pt-8 print-page-break">
            <button onClick={() => setShowChat(!showChat)} className="w-full py-4 bg-sage-50 hover:bg-sage-100 text-sage-700 rounded-2xl font-bold uppercase tracking-widest text-xs transition-colors no-print border border-sage-200">
              {showChat ? 'Hide Full Chat History' : 'View Full Chat History'}
            </button>
            <h3 className="hidden print-only text-lg font-bold text-sage-800 uppercase tracking-widest mb-6 border-b border-sage-200 pb-2">Full Chat Transcript</h3>
            
            {(showChat || false) && (
              <div className="mt-6 space-y-4 print-only-force-show">
                {data.chat_history?.map((msg: any, idx: number) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-3xl p-5 text-sm ${msg.role === 'user' ? 'bg-sage-600 text-white shadow-md' : 'bg-white text-sage-800 border border-sage-200 shadow-sm'}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </div>
                  </div>
                ))}
                <div className="hidden print-only mt-12 pt-4 border-t border-sage-200 text-center text-xs font-bold text-sage-500 uppercase tracking-widest">
                  NatureNani &mdash; Know Your Nature &mdash; naturenani.com
                </div>
              </div>
            )}
            
            <style dangerouslySetInnerHTML={{__html: `
              @media print {
                .print-only-force-show { display: block !important; }
              }
            `}} />
          </div>

        </div>
      </div>
    </div>
  );
};
